import { useEffect, useState } from "react";
import { api, apiErr } from "@/api";
import Shell from "@/components/Shell";
import { exhibitorNav } from "./ExhibitorDashboard";
import { Spinner, Overline } from "@/components/Brand";
import { toast } from "sonner";
import { UserPlus, Trash2, Users2 } from "lucide-react";

export default function ExhibitorStaff() {
  const [staff, setStaff] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [name, setName] = useState("");
  const [role, setRole] = useState("Sales Executive");
  const [phone, setPhone] = useState("");
  const [aName, setAName] = useState("");
  const [aEmail, setAEmail] = useState("");
  const [aPass, setAPass] = useState("");
  const [aRole, setARole] = useState("sales");

  const load = async () => {
    const { data } = await api.get("/exhibitor/staff");
    setStaff(data);
    try { const { data: a } = await api.get("/exhibitor/staff-accounts"); setAccounts(a); } catch {}
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/exhibitor/staff", { name, role, phone });
      toast.success("Staff added");
      setName(""); setPhone("");
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const addAccount = async (e) => {
    e.preventDefault();
    if (!aEmail.trim() || !aPass.trim()) return;
    try {
      await api.post("/exhibitor/staff-accounts", { name: aName, email: aEmail, password: aPass, staff_role: aRole });
      toast.success("Staff login created");
      setAName(""); setAEmail(""); setAPass("");
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const delAccount = async (id) => { try { await api.delete(`/exhibitor/staff-accounts/${id}`); toast.success("Removed"); load(); } catch (err) { toast.error(apiErr(err)); } };

  const del = async (id) => { await api.delete(`/exhibitor/staff/${id}`); toast.success("Removed"); load(); };

  return (
    <Shell nav={exhibitorNav}>
      <Overline>Team</Overline>
      <h1 className="font-display text-3xl gold-text mt-2">Staff Members</h1>
      <p className="text-slate-400 text-sm mt-1">Add sales executives working under your stall.</p>

      <form onSubmit={add} className="glass rounded-2xl p-5 mt-6 space-y-3">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="w-full bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid="staff-name-input" />
        <div className="grid grid-cols-2 gap-3">
          <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Role" className="bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid="staff-role-input" />
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)" className="bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid="staff-phone-input" />
        </div>
        <button className="w-full btn-gold py-2.5 rounded-xl flex items-center justify-center gap-2" data-testid="staff-add-button"><UserPlus size={16} /> Add Staff</button>
      </form>

      {!staff ? <Spinner /> : (
        <div className="space-y-3 mt-6">
          {staff.map((s) => (
            <div key={s.id} className="glass rounded-2xl p-4 flex items-center gap-3" data-testid={`staff-${s.id}`}>
              <div className="h-11 w-11 rounded-full bg-amber-500/10 flex items-center justify-center"><Users2 size={18} className="text-amber-400" /></div>
              <div className="flex-1">
                <div className="text-slate-100 font-medium">{s.name}</div>
                <div className="text-xs text-slate-400">{s.role}{s.phone ? ` · ${s.phone}` : ""}</div>
              </div>
              <button onClick={() => del(s.id)} className="h-9 w-9 rounded-lg glass flex items-center justify-center text-red-300" data-testid={`staff-del-${s.id}`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}

      <h2 className="font-display text-2xl gold-text mt-10">Staff Logins & Permissions</h2>
      <p className="text-slate-400 text-sm mt-1">Give team members their own login. Roles: owner (full), sales (catalogues/leads/scan/network), scanner-only (scan only).</p>
      <form onSubmit={addAccount} className="glass rounded-2xl p-5 mt-4 space-y-3">
        <input value={aName} onChange={(e) => setAName(e.target.value)} placeholder="Name" className="w-full bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid="staff-account-name" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input value={aEmail} onChange={(e) => setAEmail(e.target.value)} placeholder="Login email" className="bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid="staff-account-email" />
          <input value={aPass} onChange={(e) => setAPass(e.target.value)} placeholder="Temp password" className="bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid="staff-account-password" />
        </div>
        <select value={aRole} onChange={(e) => setARole(e.target.value)} className="w-full bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid="staff-account-role">
          <option value="owner">Owner / Manager</option>
          <option value="sales">Sales Staff</option>
          <option value="scanner_only">Scanner Only</option>
        </select>
        <button className="w-full btn-gold py-2.5 rounded-xl flex items-center justify-center gap-2" data-testid="staff-account-add"><UserPlus size={16} /> Create Login</button>
      </form>
      <div className="space-y-3 mt-4">
        {accounts.map((a) => (
          <div key={a.id} className="glass rounded-2xl p-4 flex items-center gap-3" data-testid={`staff-account-${a.id}`}>
            <div className="h-11 w-11 rounded-full bg-amber-500/10 flex items-center justify-center"><Users2 size={18} className="text-amber-400" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-slate-100 font-medium truncate">{a.name} <span className="text-[10px] uppercase tracking-wide text-amber-300">· {a.role === "exhibitor" ? "owner" : (a.staff_role || "sales")}</span></div>
              <div className="text-xs text-slate-400 truncate">{a.email}</div>
            </div>
            {a.role !== "exhibitor" && <button onClick={() => delAccount(a.id)} className="h-9 w-9 rounded-lg glass flex items-center justify-center text-red-300" data-testid={`staff-account-del-${a.id}`}><Trash2 size={15} /></button>}
          </div>
        ))}
      </div>
    </Shell>
  );
}
